import { useMemo } from "react";
import { makeArrayFromLength } from "oolib";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetTopicClusteringTree } from "@/utils/react-query-hooks/aiAnnotation";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { buildTopicClusteringFilterConfig, resolveTopicClusteringLens } from "@okf/ootils";

const COMMON = {
  isAnno: true, // annotations collection — the only one the BE accepts this dimension on
  dimensionType: "topicClusteringNode",
  iconName: "ShareNetwork",
};

// "Topics" — bars = every topic. The Topics folder's body-select.
const buildRootOption = (topicsFilter) => ({
  ...COMMON,
  display: "Topics",
  desc: "Topic clustering",
  value: "TOPIC_CLUSTERING_NODE__topic",
  level: "topic",
  filterKey: topicsFilter.filterKey,
});

// "All sub-topics" — bars = every sub-topic across every topic, the prevalence
// panel's sub-topic dimension (no parentTopicNodeId, so the BE buckets the
// whole tree). The cascade picker files it inside the Topics folder, first,
// beside the per-topic drills.
const buildAllSubtopicsOption = (topicsFilter) => ({
  ...COMMON,
  display: "All sub-topics",
  desc: "Topic clustering",
  value: "TOPIC_CLUSTERING_NODE__subtopic",
  level: "subtopic",
  filterKey: topicsFilter.filterKey,
});

// A topic drill — bars = this topic's sub-topics (its parked observations as
// "Unplaced"). Same two-part contract as a sub-theme drill: the preset filter
// narrows the DOCUMENTS to the topic's annotations (so percentages read as a
// share of the topic), `parentTopicNodeId` has the BE restrict the BARS to the
// topic's children so nothing outside it leaks in as a root Unplaced bar.
const buildTopicDrillOption = (topic, topicsFilter) => ({
  ...COMMON,
  display: topic.label,
  desc: "Topic",
  value: `TOPIC_CLUSTERING_NODE__subtopic__${topic.nodeId}`,
  level: "subtopic",
  parentTopicNodeId: String(topic.nodeId),
  filterKey: topicsFilter.filterKey,
  presetActiveFilters: [
    {
      filterId: topicsFilter.filterId,
      filterKey: topicsFilter.filterKey,
      values: [{ value: String(topic.nodeId) }],
      target: topicsFilter.target,
    },
  ],
});

/**
 * The Topics dimension options (split mode): the root "Topics" option plus
 * one drill option per topic in the tenant's clustering tree, which the
 * cascade picker folds into its Topics folder (root = the folder's
 * body-select, drills = its children). Every widget's request builder
 * routes `dimensionType: "topicClusteringNode"` through annoDimForRequest,
 * so the dimension-options hooks always include these. Split mode only;
 * [] otherwise, and the tree query is not fired.
 *
 * The tree is the same cached query the Topics tab and filter use, on the
 * same resolved lens. The Topics filter config comes from the same ootils
 * builder the sidebar uses, so the ids a drill's preset filter and a bar
 * click ride on can never drift from the filter they target. While the tree
 * loads, skeleton rows stand in for the drills, like the sub-theme options.
 */
export const useTopicClusteringDimensionOptions = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const isSplitMode = getAnnoMode(platformConfigs) === "split";
  const topicClusteringLens = resolveTopicClusteringLens({ platformConfigs_ai: platformConfigs?.ai });
  const { data: treeData, status } = useGetTopicClusteringTree({ topicClusteringLens, enabled: isSplitMode });

  return useMemo(() => {
    if (!isSplitMode) return [];
    const topicsFilter = buildTopicClusteringFilterConfig({ topicClusteringLens });
    const root = buildRootOption(topicsFilter);
    const allSubtopics = buildAllSubtopicsOption(topicsFilter);
    if (status === "loading") {
      return [
        root,
        allSubtopics,
        ...makeArrayFromLength(3).map((_, i) => ({ ...COMMON, loading: true, value: `__topic_loading_${i}` })),
      ];
    }
    return [root, allSubtopics, ...(treeData?.tree || []).map((topic) => buildTopicDrillOption(topic, topicsFilter))];
  }, [isSplitMode, status, treeData, topicClusteringLens]);
};
